<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <action
      :icon="AddIcon"
      :label="$t('pages.campaigns.driverList.modal.new.buttonText')"
      @click="openDriverModal = true"
    />
    <export
      url="/campaign_waiting_list_registrations/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <waiting-list-registration-modal
    v-model:open="openDriverModal"
    :campaign-group="modelValue"
    @saved="refetch"
  />
  <data-table
    class="mb-auto ml-12"
    v-model:sort-by="sortKey"
    v-model:sort-direction="sortDirection"
    :row-link="(reg) => ({ name: 'driver-details', params: { driverId: reg?.driver.id } })"
    :busy="loading"
    :items="records"
    :columns="columns"
    :visible-columns="visibleColumns"
    :actions="actions"
  />
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {ref, reactive, computed, unref, inject, type Ref} from 'vue';
import Action from '@components/utils/toolbar/Action.vue';
import AddIcon from '@images/panes/add.svg?component';
import { useI18n } from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, { type Column } from '@components/utils/table/Table.vue';
import {
  enumOptionsProvider,
  useProvider,
  withExtraOptions,
} from '@components/utils/table/hooks/provider';
import type { Filter } from '@/components/utils/filter/Filter.vue';
import type {
  HydraCollection,
  CampaignGroup,
  CampaignRegistration,
  CampaignWaitingListRegistration,
} from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';
import type { Address } from '@/entities/Entities';
import WaitingListRegistrationModal from '@pages/campaigns/campaignGroup/WaitingListRegistrationModal.vue';
import type { Action as ActionType } from '@utils/table/Table.vue';
import { useDelete } from '@hooks/api';
import Export from '@utils/toolbar/panes/Export.vue';
import { useQueryRef } from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';
import {format} from 'date-fns';
import globalFilterKey from '@utils/globalFilter/injects';

interface Props {
  modelValue: CampaignGroup;
}

const props = defineProps<Props>();

const campaignGroupNeedsLoading = computed(() => {
  return !props.modelValue;
});

const globalFilter = inject(globalFilterKey) as unknown as Ref<{ regions: {label: string, value: string}[] }>;

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 10);

const openDriverModal = ref(false);

const sortKey = useQueryRef<string | undefined>('sortKey', undefined);
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', undefined);

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.waitingListsFromGroup', {
    group: props.modelValue?.name,
  });
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  'campaign.group': props.modelValue?.id ?? '',
  'inArray[campaign.region]': globalFilter?.value?.regions,
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

const [deleteCampaignRegistration, { loading: deleting }] = useDelete();

const actions: ActionType[] = [
  {
    title: t('pages.campaigns.waitingList.actions.delete'),
    action: async (item: CampaignWaitingListRegistration) => {
      if (await deleteCampaignRegistration(`/campaign_waiting_list_registrations/${item.id}`)) {
        // eslint-disable-next-line no-use-before-define
        await refetch();
      }
    },
  },
];

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    group: t('pages.campaigns.waitingList.fieldGroups.general'),
    class: 'whitespace-nowrap',
  },
  {
    key: 'driver.user.salutation',
    csvTranslations: {
      male: t('title.male'),
      female: t('title.female'),
      other: t('title.other'),
      unknown: t('title.unknown'),
    },
    title: t('driver.title'),
    group: t('pages.campaigns.waitingList.fieldGroups.general'),
    visible: true,
    formatter: (v: string) => t(`title.${v}`),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.waitingList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/salutation', (v) => ({
          label: t(`title.${v}`),
          value: v,
        })),
        label: t('driver.title'),
      },
    },
  },
  {
    key: 'driver.user.firstName',
    title: t('user.firstName'),
    sortable: true,
    visible: true,
    group: t('pages.campaigns.waitingList.fieldGroups.general'),
    filter: {
      type: 'text',
      group: t('pages.campaigns.waitingList.filterGroups.general'),
      options: {
        label: t('user.firstName'),
      },
    },
  },
  {
    key: 'driver.user.lastName',
    title: t('user.lastName'),
    group: t('pages.campaigns.waitingList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      type: 'text',
      group: t('pages.campaigns.waitingList.filterGroups.general'),
      options: {
        label: t('user.lastName'),
      },
    },
  },
  {
    key: 'campaign.name',
    title: t('campaign.DEFAULT'),
    group: t('pages.campaigns.waitingList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      key: 'campaign',
      type: 'dropdown',
      group: t('pages.campaigns.waitingList.filterGroups.general'),
      options: {
        options: computed(() =>
          props.modelValue?.campaigns?.map((value) => ({
            label: value.name,
            value: value.id,
          }))
        ),
        label: t('campaign.DEFAULT'),
      },
    },
  },
  {
    key: 'campaign.region.name',
    title: t('campaign.region'),
    group: t('pages.campaigns.waitingList.fieldGroups.general'),
    sortable: true,
    visible: true,
  },
  {
    key: 'driver.box.status',
    csvTranslations: {
      null: t('box.status.noBox'),
      noBox: t('box.status.noBox'),
      Init: t('box.status.Init'),
      Mounted: t('box.status.Mounted'),
      Destroyed: t('box.status.Destroyed'),
      Other: t('box.status.Other'),
      Lost: t('box.status.Lost'),
      Returned: t('box.status.Returned'),
    },
    title: t('box.status.DEFAULT'),
    group: t('pages.campaigns.waitingList.fieldGroups.box'),
    sortable: true,
    visible: true,
    formatter: (v: string) => v ? t(`box.status.${v}`) : t('box.status.noBox'),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.waitingList.filterGroups.box'),
      options: {
        options: withExtraOptions(enumOptionsProvider('/enums/BoxStatus'), [
          { label: t('box.status.noBox'), value: 'noBox' },
        ]),
        valueSetter: (m: Record<string, unknown>, key: string, value: string) => {
          if (value === 'noBox') {
            delete m['box.status'];
            m['exists[driver.box]'] = false;
          } else {
            delete m['exists[driver.box]'];
            m[key] = value;
          }
        },
        valueGetter: (m: Record<string, unknown>, key: string) => {
          if ('exists[driver.box]' in m) {
            return 'noBox';
          }
          return m[key];
        },
        label: t('box.status.DEFAULT'),
      },
    },
  },
  {
    key: 'driver.defaultSourceAddress',
    title: `${t('driver.mostFrequentPath')} ${t('from')}`,
    group: t('pages.campaigns.waitingList.fieldGroups.frequency'),
    sortable: true,
    sortKey: 'driver.defaultSourceAddress.zip',
    visible: true,
    formatter: (addr: Address) =>
      (addr && addr.street && addr.zip && `${addr.street}, ${addr.zip}`) ||
      addr?.street ||
      addr?.zip,
    filter: [
      {
        type: 'linegroup',
        group: t('pages.campaigns.waitingList.fieldGroups.frequency'),
        options: {
          filters: [
            {
              key: 'driver.defaultSourceAddress.street',
              type: 'text',
              group: t('pages.campaigns.waitingList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.street'),
                label: `${t('driver.mostFrequentPath')} ${t('from')}`,
              },
            },
            {
              key: 'driver.defaultSourceAddress.city',
              type: 'text',
              group: t('pages.campaigns.waitingList.fieldGroups.frequency'),
              options: {
                placeholder: t('address.city'),
              },
            },
          ],
        },
      },
    ],
  },
  {
    key: 'driver.defaultDestinationAddress',
    title: `${t('driver.mostFrequentPath')} ${t('to')}`,
    group: t('pages.campaigns.waitingList.fieldGroups.frequency'),
    sortable: true,
    sortKey: 'driver.defaultDestinationAddress.zip',
    visible: true,
    formatter: (addr: Address) =>
      (addr && addr.street && addr.zip && `${addr.street}, ${addr.zip}`) ||
      addr?.street ||
      addr?.zip,
    filter: [
      {
        type: 'linegroup',
        group: t('pages.campaigns.waitingList.fieldGroups.frequency'),
        options: {
          filters: [
            {
              key: 'driver.defaultDestinationAddress.street',
              type: 'text',
              options: {
                placeholder: t('address.street'),
                label: `${t('driver.mostFrequentPath')} ${t('to')}`,
              },
            },
            {
              key: 'driver.defaultDestinationAddress.city',
              type: 'text',
              options: {
                placeholder: t('address.city'),
              },
            },
          ],
        },
      },
    ],
  },
  {
    key: 'driver.bicycleType',
    title: t('driver.bicycleType'),
    group: t('pages.campaigns.waitingList.fieldGroups.general'),
    sortable: true,
    visible: true,
    csvTranslations: {
      null: t('driver.bicycleTypes.null'),
      RB: t('driver.bicycleTypes.RB'),
      MTB: t('driver.bicycleTypes.MTB'),
      EMTB: t('driver.bicycleTypes.EMTB'),
      CB: t('driver.bicycleTypes.CB'),
      ECB: t('driver.bicycleTypes.ECB'),
      SS: t('driver.bicycleTypes.SS'),
    },
    formatter: (v: string) => v ? t(`driver.bicycleTypes.${v}`) : t('driver.bicycleTypes.null'),
    filter: {
      type: 'dropdown',
      group: t('pages.campaigns.waitingList.filterGroups.general'),
      options: {
        options: enumOptionsProvider('/enums/Bicycle',(v) => ({
          value: v,
          label: t(`driver.bicycleTypes.${v}`),
        })),
        label: t('driver.bicycleType'),
      },
    },
  },
  {
    key: 'driver.specialDriverPhoto',
    title: t('driver.specialPhoto'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('driver.specialPhoto'),
      },
    }
  },
  {
    key: 'driver.specialDriverStanding',
    title: t('driver.specialStanding'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('driver.specialStanding'),
      },
    }
  },
  {
    key: 'driver.helperDriver',
    title: t('driver.helper'),
    group: t('pages.driverList.filterGroups.special'),
    sortable: true,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaigns.driverList.filterGroups.general'),
      options: {
        label: t('driver.helper'),
      },
    },
  },
  {
    key: 'driver.liabilityDisclaimerSigned',
    csvTranslations: {
      true: t('yes'),
      false: t('no'),
    },
    title: t('driver.liabilityDisclaimerSigned'),
    group: t('pages.campaigns.waitingList.fieldGroups.general'),
    sortable: true,
    visible: false,
    formatter: (v: boolean) => v ? t('yes') : t('no'),
    filter: {
      type: 'bool',
      group: t('pages.campaigns.waitingList.filterGroups.general'),
      options: {
        label: t('driver.liabilityDisclaimerSigned'),
      },
    },
  },
  {
    key: 'driver.endDateLastFinishedCampaign',
    title: t('driver.endDateLastFinishedCampaign'),
    group: t('pages.campaigns.waitingList.fieldGroups.general'),
    sortable: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '-',
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.waitingList.filterGroups.general'),
      options: {
        label: t('driver.endDateLastFinishedCampaign'),
      },
    },
  },
  {
    key: 'driver.endDateLastRegisteredCampaign',
    title: t('driver.endDateLastRegisteredCampaign'),
    group: t('pages.campaigns.waitingList.fieldGroups.general'),
    sortable: true,
    formatter: (v: string) => v ? format(new Date(v), 'dd.MM.yyyy') : '-',
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.waitingList.filterGroups.general'),
      options: {
        label: t('driver.endDateLastRegisteredCampaign'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<CampaignWaitingListRegistration>>(
  '/campaign_waiting_list_registrations',
  providerParams,
  { disabled: campaignGroupNeedsLoading }
);

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);
</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>
